import { AggregatePin, CustomerForMap } from 'app/models';

import { AccountId, RuleId } from './MapActorV2';
import { RuleLookups } from './RuleLookups';

export class AccountLookups {
  private readonly accountsMap = new Map<AccountId, CustomerForMap>();
  private readonly allAccounts: ReadonlyArray<CustomerForMap>;
  private readonly featuresByRuleId = new Map<RuleId, Array<number>>();

  constructor(
    private readonly rawAccounts: ReadonlyArray<AggregatePin>,
    ruleLookups: RuleLookups
  ) {
    for (const { accountId, geographyId, accountName, measureValue, center } of rawAccounts) {
      if (ruleLookups.isAccountReserved(accountId)) continue;
      const ruleId = ruleLookups.calculateAccountAssignment(accountId, geographyId);
      const isCapturedByOverride = ruleLookups.hasAccountOverride(accountId);
      const rule = ruleId && ruleLookups.getRule(ruleId);
      const geoOverassignmentCount = isCapturedByOverride ? 0 : ruleLookups.getOverassignedGeoCount(geographyId);
      const accountOverassignmentCount = isCapturedByOverride ? 0 : ruleLookups.getOverassignedAccountCount(accountId);
      const customer: CustomerForMap = {
        center: [center.lon, center.lat],
        accountId,
        geographyId,
        accountName,
        ruleId,
        measureValue,
        isModifierAccount: !!isCapturedByOverride,
        ruleColor: rule?.color ?? null,
        territoryName: rule?.territoryName ?? null,
        territoryId: rule?.territoryId ?? null,
        isGeoOverassigned: !!geoOverassignmentCount,
        geoOverassignmentCount,
        accountOverassignmentCount
      };
      this.accountsMap.set(accountId, customer);
      if (!accountOverassignmentCount) {
        this.addToRuleFeatures(ruleId, accountId);
      }
    }
    this.allAccounts = [...this.accountsMap.values()];
  }

  private addToRuleFeatures(ruleId: RuleId, accountId: AccountId) {
    if (this.featuresByRuleId.has(ruleId)) {
      this.featuresByRuleId.get(ruleId).push(accountId);
    } else {
      this.featuresByRuleId.set(ruleId, [accountId]);
    }
  }

  public getAccount(accountId: AccountId) {
    return this.accountsMap.get(accountId);
  }

  public getAllAccounts(): ReadonlyArray<CustomerForMap> {
    return this.allAccounts;
  }

  public getRawAccounts() {
    return this.rawAccounts;
  }

  public getFeaturesByRuleId(ruleId: RuleId): ReadonlyArray<number> {
    return this.featuresByRuleId.get(ruleId) ?? [];
  }

  public getCount() {
    return this.accountsMap.size;
  }
}
